import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import * as m from '../../../../localization/out/messages.js';

export function buildEmbedSnippet(origin: string, page: string, tag: string): string {
  return `<script src="${origin}/api/embed-source/${page}"></script>\n<${tag}></${tag}>`;
}

const EmbedSnippetCard = stateful(
  ({ title, description, snippet }: { title: string; description: string; snippet: string }) => {
    const [copied, setCopied] = useState(false, []);
    const [copying, setCopying] = useState(false, []);
    const code = useMemo(() => [createRef<HTMLTextAreaElement>()], []);

    useEffect(() => {
      if (!copied) return;
      const timeout = window.setTimeout(() => setCopied(false), 2000);
      return () => window.clearTimeout(timeout);
    }, [copied]);

    const copy = async () => {
      setCopying(true);
      try {
        await navigator.clipboard.writeText(snippet);
        setCopied(true);
      } catch {
        code.value?.focus();
        code.value?.select();
      } finally {
        setCopying(false);
      }
    };

    return html`
      <section class="card bg-base-100 shadow-xl">
        <div class="card-body min-w-0">
          <h2 class="card-title text-2xl">${title}</h2>
          <p class="text-sm text-base-content/70">${description}</p>
          <textarea
            ${ref(code)}
            class="textarea textarea-bordered bg-base-200 w-full font-mono text-sm mt-4"
            aria-label=${title}
            readonly
            spellcheck="false"
            rows="3"
            wrap="off"
            .value=${snippet}
          ></textarea>
          <div class="card-actions justify-end mt-4">
            <button type="button" class="btn btn-primary" ?disabled=${copying || copied} @click=${copy}>
              <span class="icon-[material-symbols--content-copy]" aria-hidden="true"></span>
              <span aria-live="polite">${copied ? m.admin_embed_copied() : m.admin_embed_copy()}</span>
            </button>
          </div>
        </div>
      </section>
    `;
  },
);

export const AdminEmbedSnippets = stateful(() => {
  const origin = window.location.origin;

  return html`
    <div class="p-4 sm:p-8 flex flex-col items-center">
      <div class="max-w-4xl w-full space-y-6">
        <div>
          <h1 class="text-3xl font-bold">${m.admin_embed_title()}</h1>
          <p class="text-base-content/70 mt-2">${m.admin_embed_intro()}</p>
        </div>
        ${EmbedSnippetCard({
          title: m.admin_embed_cv_check_title(),
          description: m.admin_embed_cv_check_description(),
          snippet: buildEmbedSnippet(origin, 'cv-check', 'cv-check-embed'),
        })}
        ${EmbedSnippetCard({
          title: m.admin_embed_registration_title(),
          description: m.admin_embed_registration_description(),
          snippet: buildEmbedSnippet(origin, 'index', 'matching-week-embed'),
        })}
      </div>
    </div>
  `;
});
